/* ============================================================
   Hue Ghost page controller.

   A model of the real program, drawn in the shared lit room (oss-room.js).
   Sourced from the HueGhost README (2.10.x):

   - The TV plays the film. The ghost plays the same film on a PC display,
     `offset` seconds ahead, and Hue Sync captures the ghost. The capture ->
     bridge -> lamp chain (plus the TV's display lag) eats that lead, so a
     TUNED offset lands the lamps on every cut. The demo starts tuned, as a
     real install is after its one-time tuning; the stepper lets a visitor
     knock it off and bring it back.
   - Time lock (experimental, Sync page): once drift reaches 0.00 s the
     timeline is held and the ghost plays at 1.0x ("drift +0.00 s · locked").
     Off, the ghost is steered by small speed nudges (up to +/-4 %).
   - After a seek the TV buffers; the ghost is held for a LEARNED stall,
     then resumes on target, and the estimate is refined per TV.
   - Stop: lights off 1.5 s later; the ghost waits 10 s on standby.
   ============================================================ */

const INSTANCES = new WeakMap();
let ROOM = null;   /* the oss-room module, loaded once */

const TITLE = 'Nightfall';
const CHAPTER0 = 42 * 60 + 18;

const SOURCES = [
  { id: 'atv', name: 'Apple TV', meta: 'Jellyfin client · followed by its device id', kind: 'client', mode: 'Automatic', modeNow: 'video, because it is a film', intensity: 'High', area: 'Lounge room', how: 'The ghost mirrors the film on the virtual display, in step with the TV.' },
  { id: 'phone', name: 'S23 music', meta: 'Android · CAMusic, renamed so it is obvious', kind: 'music', mode: 'Automatic', modeNow: 'music, because it is a song', intensity: 'High', area: 'Office', how: 'The ghost plays the sound to a spare audio output, and Hue Sync listens in music mode.' },
  { id: 'chrome', name: 'Google Chrome', meta: 'App on this PC · counts as playing when fullscreen', kind: 'pc', mode: 'Video', modeNow: 'video', intensity: 'Subtle', area: 'Office', how: 'The picture is already on a screen, so Hue Sync captures your monitor directly.' },
  { id: 'game', name: 'Racing game', meta: 'App on this PC · game.exe, counts as playing when fullscreen', kind: 'pc', mode: 'Games', modeNow: 'games', intensity: 'Extreme', area: 'Gaming corner', how: 'Hue Sync captures the monitor the game is on.' }
];

/* the app's own colours for modes and intensities (hueghost/gui/theme.py) */
const MODE_C = { video: '#60a5fa', music: '#a78bfa', games: '#3ddc97' };
const INT_C = { subtle: '#38bdf8', moderate: '#a78bfa', high: '#f472b6', extreme: '#fb7185' };
const SRC_ICON = {
  atv: '<svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8.5 20h7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  phone: '<svg viewBox="0 0 24 24"><rect x="7" y="2.5" width="10" height="19" rx="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M11 18.3h2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  chrome: '<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="12.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M9 20.5h6M12 16.5v4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  game: '<svg viewBox="0 0 24 24"><path d="M7.2 8.5h9.6a4.2 4.2 0 0 1 4.1 5.1l-.7 3.1a2.4 2.4 0 0 1-4.2 1L14.6 16H9.4L8 17.7a2.4 2.4 0 0 1-4.2-1l-.7-3.1a4.2 4.2 0 0 1 4.1-5.1Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M8 11.3v3M6.5 12.8h3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="15.6" cy="12" r="1" fill="currentColor"/><circle cx="17.4" cy="13.8" r="1" fill="currentColor"/></svg>'
};

/* each source as Hue Synco's Hue Ghost device reports it: its switch, the
   kind the card picks an icon by, and what the integration says is playing */
const HA_SRC = {
  atv: { slug: 'apple_tv', kind: 'jellyfin', device: 'Apple TV 4K', title: 'Sintel' },
  phone: { slug: 's23_music', kind: 'jellyfin', device: 'Galaxy S23', title: 'Good Vibes' },
  chrome: { slug: 'google_chrome', kind: 'pc', device: 'chrome.exe', title: 'Big Buck Bunny · Google Chrome' },
  game: { slug: 'racing_game', kind: 'pc', device: 'game.exe', title: 'Racing game' }
};
/* the integration's own labels (translations/en.json) */
const GHOST_TR = {
  ghost_state: { offline: 'Offline', idle: 'Idle', ghosting: 'Ghosting', syncing: 'Syncing' },
  ghost_mode: { video: 'Video (picture)', music: 'Music (sound)', games: 'Games' },
  ghost_intensity: { subtle: 'Subtle', moderate: 'Moderate', high: 'High', extreme: 'Extreme' }
};
/* the rows of an entities card for the same device */
const ENT_ROWS = [
  ['light.hue_ghost_global_sync', 'Global sync', 'toggle'],
  ['sensor.hue_ghost_sync_status', 'Sync status'],
  ['sensor.hue_ghost_sync_area', 'Sync area'],
  ['sensor.hue_ghost_active_source', 'Active source'],
  ['sensor.hue_ghost_now_playing', 'Now playing'],
  ['select.hue_ghost_mode', 'Mode', 'select'],
  ['select.hue_ghost_intensity', 'Intensity', 'select'],
  ['number.hue_ghost_sync_offset', 'Sync offset', 'box']
];
const modeOf = (src) => (src.modeNow.split(',')[0] || '').trim();
const abs = (u) => (/^(https?:)?\/\//.test(u) ? u : new URL(u, document.baseURI).href);

const fmtS = (v) => (v < -0.004 ? '−' : '+') + Math.abs(v).toFixed(2) + ' s';
const fmtClock = (s) => {
  s = Math.max(0, Math.floor(s));
  return Math.floor(s / 3600) + ':' + String(Math.floor((s % 3600) / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
};

class HueGhostPage {
  constructor(root, cfg) {
    this.root = root;
    this.cfg = cfg || {};
    this.refs = {};
    this.listeners = [];
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* this room's chain delay, and the offset it was tuned to once */
    this.CHAIN = Math.round((1.15 + Math.random() * 0.5) / 0.05) * 0.05;
    this.tuned = this.CHAIN;
    this.offset = this.tuned;
    this.timeLock = true;
    this.locked = false;
    this.lockHold = 0;

    this.phase = 'idle';
    this.tvT = 0; this.tvRun = false; this.tvStall = 0;
    this.ghostOpen = false; this.ghostT = 0; this.ghostRun = false; this.ghostHold = 0;
    this.speed = 1;
    this.tvBuffer = 3.5 + Math.random() * 0.6;            /* how long THIS TV buffers */
    this.learned = Math.round((this.tvBuffer + 0.35) * 10) / 10;
    this.pendingSeek = null;
    this.wander = 0;
    this.lightsK = 0; this.lightsTarget = 0;
    this.timer = { lightsOff: 0, standby: 0, starting: 0 };
    this.lampL = [0, 0, 0]; this.lampR = [0, 0, 0]; this.lampT = [0, 0, 0];
    this.ghostHist = [];
    this.lanes = [];
    this.lastTvCut = null; this.lastLampCut = null;
    this.driftShown = 0;
    this.autoplayed = false;

    this.order = SOURCES.map((s) => s.id);
    this.enabled = { atv: true, phone: true, chrome: true, game: true };
    this.playing = { atv: true, phone: true, chrome: false, game: false };
    this.prevWinner = null;

    /* Home Assistant's view: the Global sync light is movie mode, its
       brightness the level Hue Sync runs the area at; mode and intensity
       changed there last for the session; the status sensor walks idle,
       ghosting, syncing */
    this.movie = true;
    this.level = 62;
    this.haOffset = 1.5;
    this.session = null;
    this.lastHs = { mode: 'video', intensity: 'high' };
    const w0 = this.winner();
    this.hs = w0 ? 'syncing' : 'idle';
    this.hsFor = w0 ? w0.id : null;
  }

  on(t, ty, fn, o) { t.addEventListener(ty, fn, o); this.listeners.push([t, ty, fn, o]); }

  mount() {
    /* the evening demo nested in this page has its own controller and refs */
    this.root.querySelectorAll('[data-ref]').forEach((el) => { if (!el.closest('[data-osshub]')) this.refs[el.getAttribute('data-ref')] = el; });
    this.on(this.root, 'click', (e) => {
      const el = e.target.closest('[data-act]');
      if (!el || !this.root.contains(el) || el.disabled || el.closest('[data-osshub]')) return;
      const fn = HueGhostPage.ACTIONS[el.getAttribute('data-act')];
      if (fn) fn.call(this, el, e);
    });
    this.applyLayout();
    this.on(window, 'resize', () => { this.applyLayout(); if (this.room) this.room.resize(); this.fitLanes(); });
    this.fitLanes();
    this.renderOffset();
    this.renderTimeLock();
    this.renderSources();
    this.mountHa();
    if (this.refs.stall) this.refs.stall.textContent = this.learned.toFixed(1) + ' s';

    this.visible = false;
    if ('IntersectionObserver' in window && this.refs.stage) {
      this.io = new IntersectionObserver((ents) => {
        this.visible = ents[0].isIntersecting;
        /* like pressing play on the couch the first time the room comes into view */
        if (this.visible && !this.autoplayed && !this.reduced && this.phase === 'idle') {
          this.autoplayed = true;
          HueGhostPage.ACTIONS.play.call(this);
        }
      }, { threshold: 0.35 });
      this.io.observe(this.refs.stage);
    } else this.visible = true;

    /* the sync model only runs where its own room is on the page */
    if (!this.refs.stage) return;
    const spec = this.cfg.room;
    const load = ROOM ? Promise.resolve(ROOM) : import(/^(https?:)?\/\//.test(spec) ? spec : new URL(spec, document.baseURI).href);
    load.then((mod) => {
      ROOM = mod;
      this.M = mod;
      this.room = new mod.Room(this.refs.stage);
      this.last = performance.now();
      const loop = (now) => {
        const dt = Math.min(0.05, (now - this.last) / 1000);
        this.last = now;
        if (this.visible && !document.hidden) { this.step(dt, now / 1000); this.draw(); }
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
      this.room.resize(); this.draw();
    }).catch((e) => console.error('[hue-ghost] room failed to load', e));
  }

  destroy() {
    this.listeners.forEach(([t, ty, fn, o]) => t.removeEventListener(ty, fn, o));
    this.listeners = [];
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    if (this.haIo) this.haIo.disconnect();
    clearTimeout(this.hsTimer);
    if (this.ents) this.ents.destroy();
  }

  applyLayout() {
    const g = this.refs.demoGrid, room = this.refs.roomCol, panel = g && g.querySelector('.cmu-demo__panel');
    if (!g) return;
    const w = g.getBoundingClientRect().width;
    if (w < 760) {
      /* stacked: the room first, the app's Home page under it */
      g.style.gridTemplateColumns = 'minmax(0,1fr)';
      if (room) { room.style.gridColumn = '1'; room.style.gridRow = '1'; }
      if (panel) { panel.style.gridColumn = '1'; panel.style.gridRow = '2'; }
    } else {
      g.style.gridTemplateColumns = 'minmax(0,1fr) minmax(0,1.9fr)';
      if (room) { room.style.gridColumn = '2'; room.style.gridRow = '1'; }
      if (panel) { panel.style.gridColumn = '1'; panel.style.gridRow = '1'; }
    }
  }

  fitLanes() {
    const c = this.refs.lanes;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1), r = c.getBoundingClientRect();
    c.width = Math.max(1, Math.round(r.width * dpr)); c.height = Math.max(1, Math.round(r.height * dpr)); c._dpr = dpr;
  }

  /* ================= the model ================= */

  step(dt, wall) {
    const M = this.M, T = this.timer;
    if (this.tvRun) {
      if (this.tvStall > 0) this.tvStall = Math.max(0, this.tvStall - dt);
      else this.tvT += dt;
    }

    if (this.pendingSeek) {
      this.pendingSeek.wait -= dt;
      if (this.pendingSeek.wait <= 0) {
        const s = this.pendingSeek;
        this.pendingSeek = null;
        this.ghostT = s.target + this.offset;
        this.ghostHold = Math.max(0, this.learned - 0.5);
        this.setVerdict('Seek followed within one poll. The ghost holds for this TV’s learned <b>' + this.learned.toFixed(1) + ' s</b> of buffering, then resumes on target.');
        this.learned = Math.round((this.learned * 0.5 + s.actual * 0.5) * 10) / 10;
        if (this.refs.stall) this.refs.stall.textContent = this.learned.toFixed(1) + ' s';
        this.unlock();
      }
    }

    if (this.ghostOpen && this.ghostRun) {
      if (this.ghostHold > 0) {
        this.ghostHold = Math.max(0, this.ghostHold - dt);
      } else {
        const target = this.tvT + this.offset;
        let d = this.ghostT - target;
        if (Math.abs(d) > 1 && !this.pendingSeek && this.tvStall <= 0) { this.ghostT = target; d = 0; }
        if (this.locked) {
          this.speed = 1;
          this.ghostT = target;                             /* timeline held */
        } else {
          /* reports arrive a few times a second; each moves the estimate a hair */
          this.wander += (Math.random() - 0.5) * (this.timeLock ? 0.004 : 0.012);
          this.wander *= 0.96;
          this.speed = 1 + Math.max(-0.04, Math.min(0.04, -d * 1.2));
          this.ghostT += dt * this.speed + this.wander * dt;
          if (this.timeLock && this.tvStall <= 0 && Math.abs(d) < 0.01) {
            this.lockHold += dt;
            if (this.lockHold > 1.2) { this.locked = true; this.ghostT = target; }
          } else this.lockHold = 0;
        }
      }
    } else this.speed = 1;
    if (this.ghostOpen) {
      this.ghostHist.push([wall, this.ghostT]);
      while (this.ghostHist.length && this.ghostHist[0][0] < wall - 4) this.ghostHist.shift();
    }

    if (this.phase === 'starting') { T.starting -= dt; if (T.starting <= 0) { this.phase = 'syncing'; this.lightsTarget = 1; this.renderState(); } }
    if (this.phase === 'stopped') { T.lightsOff -= dt; if (T.lightsOff <= 0) { this.lightsTarget = 0; this.phase = 'standby'; T.standby = 10; this.renderState(); } }
    if (this.phase === 'standby') {
      T.standby -= dt;
      if (T.standby <= 0) { this.ghostOpen = false; this.ghostHist = []; this.phase = 'idle'; this.renderState(); }
      else if (this.refs.npTime) this.refs.npTime.textContent = 'Lights off. Ghost closes in ' + Math.ceil(T.standby) + ' s unless the TV comes straight back';
    }

    /* what Hue Sync captured from the ghost CHAIN seconds ago */
    let lamp = null;
    if (this.lightsTarget > 0 || this.lightsK > 0.01) {
      const at = wall - this.CHAIN;
      for (let i = this.ghostHist.length - 1; i >= 0; i--) if (this.ghostHist[i][0] <= at) { lamp = M.filmAt(this.ghostHist[i][1]); break; }
    }
    const kk = 1 - Math.pow(0.5, dt / 0.05);
    if (lamp) {
      this.lampL = M.mix(this.lampL, lamp.l, kk);
      this.lampR = M.mix(this.lampR, lamp.r, kk);
      this.lampT = M.mix(this.lampT, lamp.t, kk);
    }
    this.lightsK += (this.lightsTarget - this.lightsK) * (1 - Math.pow(0.5, dt / 0.3));

    const tvS = this.tvRun || this.phase === 'paused' ? M.filmAt(this.tvT) : null;
    this.tvS = tvS;
    let tvCut = false, lampCut = false;
    if (tvS && this.tvStall <= 0) { if (this.lastTvCut !== null && tvS.cut !== this.lastTvCut) tvCut = true; this.lastTvCut = tvS.cut; }
    if (lamp && this.lightsK > 0.3) { if (this.lastLampCut !== null && lamp.cut !== this.lastLampCut) lampCut = true; this.lastLampCut = lamp.cut; }
    if (tvCut && this.phase === 'syncing' && this.ghostHold <= 0) this.judgeCut();

    const k = this.lightsK;
    this.lanes.push([wall, tvS && this.tvRun ? M.mix(tvS.l, tvS.r, 0.5) : null, k > 0.02 ? M.mix(this.lampL, this.lampR, 0.5).map((v) => v * k) : null, tvCut, lampCut]);
    while (this.lanes.length && this.lanes[0][0] < wall - 8.2) this.lanes.shift();

    this.renderLive();
  }

  unlock() { this.locked = false; this.lockHold = 0; }

  judgeCut() {
    const d = this.locked ? 0 : this.ghostT - (this.tvT + this.offset);
    const lag = this.CHAIN - this.offset - d;
    const el = this.refs.verdict;
    if (!el) return;
    if (Math.abs(lag) <= 0.06) {
      el.classList.add('is-locked');
      el.innerHTML = this.offset === this.tuned
        ? '<b>On the cut.</b> Tuned once for this room, the lamps change with the picture and stay there.'
        : '<b>On the cut again.</b> That is the whole tuning job, done once and saved.';
      return;
    }
    el.classList.remove('is-locked');
    const late = lag > 0, big = Math.abs(lag) >= 0.2;
    el.innerHTML = 'Now the lamps land <b>' + Math.abs(Math.round(lag * 20) / 20).toFixed(2) + ' s ' + (late ? 'late' : 'early') + '</b>, what an untuned setup looks like. Press <b>' + (late ? '+' : '−') + (big ? '.25' : '.05') + '</b> or <b>Back to tuned</b>.';
  }

  setVerdict(html) { const el = this.refs.verdict; if (el) { el.classList.remove('is-locked'); el.innerHTML = html; } }

  /* ================= panel ================= */

  renderState() {
    const map = { idle: ['Idle', 'grey'], starting: ['Ghost playing', 'blue'], syncing: ['Syncing', 'green'], paused: ['Paused', 'blue'], stopped: ['TV stopped', 'blue'], standby: ['Ghost parked', 'grey'] };
    const m = map[this.phase] || map.idle;
    if (this.refs.state) this.refs.state.setAttribute('data-tone', m[1]);
    if (this.refs.stateText) this.refs.stateText.textContent = m[0];
    const P = this.refs.playBtn;
    if (P) {
      const live = this.phase === 'syncing' || this.phase === 'starting';
      P.textContent = live ? '❚❚ Pause' : this.phase === 'paused' ? '▶ Resume' : '▶ Play on the TV';
      P.classList.toggle('is-on', live);
    }
    const playingish = this.phase === 'syncing' || this.phase === 'starting' || this.phase === 'paused';
    if (this.refs.seekBtn) this.refs.seekBtn.disabled = this.phase !== 'syncing';
    if (this.refs.stopBtn) this.refs.stopBtn.disabled = !playingish;
    if (this.phase === 'idle') {
      if (this.refs.npTitle) this.refs.npTitle.textContent = 'Nothing on the TV';
      if (this.refs.npTime) this.refs.npTime.textContent = 'Press play below, the way you would on the couch';
    }
  }

  renderOffset() {
    if (this.refs.offset) this.refs.offset.textContent = this.offset.toFixed(2) + ' s';
    if (this.refs.tunedNote) this.refs.tunedNote.textContent = this.offset === this.tuned ? 'Tuned for this room' : 'Tuned value: ' + this.tuned.toFixed(2) + ' s';
    if (this.refs.resetBtn) this.refs.resetBtn.disabled = this.offset === this.tuned;
  }

  renderTimeLock() {
    const b = this.refs.lockBtn;
    if (b) { b.classList.toggle('is-on', this.timeLock); b.setAttribute('aria-pressed', String(this.timeLock)); }
  }

  renderLive() {
    const R = this.refs, now = performance.now();
    if (this._nextUi && now < this._nextUi) return;
    this._nextUi = now + 80;
    const hasGhost = this.ghostOpen && (this.phase === 'syncing' || this.phase === 'paused' || this.phase === 'starting');
    const d = hasGhost && !this.locked && this.ghostHold <= 0 && this.tvStall <= 0 ? this.ghostT - (this.tvT + this.offset) : 0;
    this.driftShown += (d - this.driftShown) * 0.5;
    if (Math.abs(this.driftShown) < 0.004) this.driftShown = 0;
    if (R.driftVal) R.driftVal.textContent = 'drift ' + fmtS(this.driftShown) + (this.locked && hasGhost ? ' · locked' : '');
    if (R.needle) {
      R.needle.style.left = (50 + Math.max(-1, Math.min(1, this.driftShown / 0.5)) * 48) + '%';
      R.needle.style.background = Math.abs(this.driftShown) <= 0.15 ? '#3DDC84' : '#F0B35A';
    }
    if (R.zone) { R.zone.style.left = (50 - 0.15 / 0.5 * 48) + '%'; R.zone.style.width = (0.15 / 0.5 * 96) + '%'; }
    if (R.speed) R.speed.textContent = (hasGhost ? this.speed : 1).toFixed(3) + '×';
    if (this.tvRun || this.phase === 'paused') {
      const s = this.tvS || this.M.filmAt(this.tvT);
      if (R.npTitle) R.npTitle.textContent = TITLE + ' · ' + s.f.name;
      if (R.npTime) R.npTime.textContent = this.tvStall > 0 ? 'TV buffering after the seek…'
        : this.ghostHold > 0 ? 'Ghost held for the learned buffering · ' + fmtClock(CHAPTER0 + this.tvT)
        : fmtClock(CHAPTER0 + this.tvT) + ' of 1:58:40' + (this.phase === 'paused' ? ' · paused' : '');
    }
    if (R.laneNote) {
      const lag = this.CHAIN - this.offset;
      R.laneNote.textContent = this.phase === 'syncing' ? (Math.abs(lag) <= 0.06 ? 'cuts line up' : (lag > 0 ? 'lamps trail the picture' : 'lamps run ahead of the picture')) : 'cuts line up when tuned';
    }
  }

  /* ================= drawing ================= */

  draw() {
    if (!this.room) return;
    const M = this.M, k = this.lightsK;
    const sc = (c, f = 1) => c.map((v) => v * k * f);
    const tvOn = this.tvRun || this.phase === 'paused';
    const S = this.tvS;
    const t = this.tvT;
    this.room.draw({
      lamps: {
        stripL: sc(this.lampL), stripR: sc(this.lampR), stripT: sc(this.lampT),
        barL: sc(this.lampL, 0.95), barR: sc(this.lampR, 0.95), signe: sc(M.mix(this.lampR, this.lampT, 0.4), 0.9)
      },
      tvLight: tvOn && S ? M.mix(S.l, S.r, 0.5) : null,
      tv: (ctx, x, y, w, h) => {
        if (tvOn && S) {
          M.drawFilm(ctx, x, y, w, h, S, t);
          if (this.tvStall > 0) this.spinner(ctx, x + w / 2, y + h / 2, h * 0.07);
        } else this.homeScreen(ctx, x, y, w, h);
      },
      overlay: (ctx, G, s, W) => this.ghostCard(ctx, G, s, W)
    });
    this.drawLanes();
  }

  /* the TV idles on a Jellyfin-like home screen, a row of posters */
  homeScreen(ctx, x, y, w, h) {
    const g = ctx.createLinearGradient(x, y, x, y + h);
    g.addColorStop(0, '#101624'); g.addColorStop(1, '#07090f');
    ctx.fillStyle = g; ctx.fillRect(x, y, w, h);
    const cols = [['#2a4a7a', '#12203a'], ['#7a3a2a', '#2a1210'], ['#2a6a4a', '#0e2a1c'], ['#6a2a6a', '#240c24'], ['#7a6a2a', '#2a240c']];
    for (let i = 0; i < 5; i++) {
      const pw = w * 0.16, px = x + w * 0.06 + i * (pw + w * 0.025), py = y + h * 0.42;
      const pg = ctx.createLinearGradient(px, py, px, py + pw * 1.4);
      pg.addColorStop(0, cols[i][0]); pg.addColorStop(1, cols[i][1]);
      ctx.fillStyle = pg; ctx.fillRect(px, py, pw, pw * 1.4);
      if (i === 0) { ctx.strokeStyle = 'rgba(255,255,255,0.8)'; ctx.lineWidth = Math.max(1, w * 0.004); ctx.strokeRect(px, py, pw, pw * 1.4); }
    }
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.font = '600 ' + Math.round(h * 0.075) + 'px Barlow, sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.fillText(TITLE, x + w * 0.06, y + h * 0.24);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = Math.round(h * 0.04) + 'px Inter, sans-serif';
    ctx.fillText(this.phase === 'idle' ? 'Continue watching · 42 min left' : 'Stopped', x + w * 0.06, y + h * 0.33);
  }

  spinner(ctx, x, y, r) {
    const a = (performance.now() / 1000) * 5;
    ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.beginPath(); ctx.arc(x, y, r * 1.9, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.92)'; ctx.lineWidth = Math.max(2, r * 0.22); ctx.lineCap = 'round';
    ctx.beginPath(); ctx.arc(x, y, r, a, a + Math.PI * 1.3); ctx.stroke(); ctx.lineCap = 'butt';
  }

  /* the PC's ghost display, as a small glass card in the corner */
  ghostCard(ctx, G, s, W) {
    const M = this.M;
    const small = W < 520 * s;
    const pw = small ? W * 0.26 : Math.max(120 * s, Math.min(W * 0.2, G.tvX - 40 * s)), ph = pw * 9 / 16;
    const px = (small ? 10 : 18) * s, py = (small ? 10 : 18) * s, pad = (small ? 5 : 8) * s;
    ctx.save();
    ctx.fillStyle = 'rgba(12,12,18,0.78)';
    ctx.beginPath(); ctx.roundRect ? ctx.roundRect(px, py, pw + pad * 2, ph + pad * 2 + (small ? 20 : 34) * s, 12 * s) : ctx.rect(px, py, pw + pad * 2, ph + pad * 2 + (small ? 20 : 34) * s); ctx.fill();
    ctx.strokeStyle = 'rgba(124,92,255,0.6)'; ctx.lineWidth = 1 * s; ctx.stroke();
    ctx.save(); ctx.beginPath(); ctx.rect(px + pad, py + pad, pw, ph); ctx.clip();
    if (this.ghostOpen) M.drawFilm(ctx, px + pad, py + pad, pw, ph, M.filmAt(this.ghostT), this.ghostT);
    else { ctx.fillStyle = '#07070a'; ctx.fillRect(px + pad, py + pad, pw, ph); }
    ctx.restore();
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '600 ' + Math.round(11 * s) + 'px Inter, sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.fillText('PC · ghost display', px + pad, py + pad + ph + 16 * s);
    ctx.fillStyle = 'rgba(190,176,255,0.85)';
    ctx.font = Math.round(10 * s) + 'px Inter, sans-serif';
    if (!small) ctx.fillText(this.ghostOpen ? (this.ghostHold > 0 ? 'muted · held for buffering' : 'muted · ' + this.offset.toFixed(2) + ' s ahead of the TV') : 'closed', px + pad, py + pad + ph + 29 * s);
    ctx.restore();
  }

  drawLanes() {
    const c = this.refs.lanes;
    if (!c) return;
    const ctx = c.getContext('2d'), W = c.width, H = c.height, s = c._dpr || 1;
    ctx.clearRect(0, 0, W, H);
    const now = performance.now() / 1000, span = 8, pad = 6 * s, laneH = (H - pad * 3) / 2, y1 = pad, y2 = pad * 2 + laneH;
    const rrect = (x, y, w, h, r) => { ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x, y, w, h, r) : ctx.rect(x, y, w, h); };
    ctx.fillStyle = 'rgba(255,255,255,0.035)';
    rrect(pad, y1, W - pad * 2, laneH, 8 * s); ctx.fill();
    rrect(pad, y2, W - pad * 2, laneH, 8 * s); ctx.fill();
    const L = this.lanes, xOf = (w) => pad + (W - pad * 2) * (1 - (now - w) / span);
    ctx.save(); rrect(pad, y1, W - pad * 2, y2 + laneH - y1, 8 * s); ctx.clip();
    for (let i = 0; i < L.length; i++) {
      const a = L[i], b = L[i + 1];
      const x0 = Math.max(pad, xOf(a[0])), x1 = b ? xOf(b[0]) : W - pad, ww = Math.max(1, x1 - x0 + 0.7);
      if (a[1]) { ctx.fillStyle = 'rgb(' + a[1].map(Math.round).join(',') + ')'; ctx.fillRect(x0, y1, ww, laneH); }
      if (a[2]) { ctx.fillStyle = 'rgb(' + a[2].map(Math.round).join(',') + ')'; ctx.fillRect(x0, y2, ww, laneH); }
    }
    /* soft sheen, so the strips read as light rather than paint */
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(0.5, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(0,0,0,0.18)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    for (const a of L) {
      const x = xOf(a[0]);
      if (x < pad) continue;
      if (a[3]) { ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.fillRect(x - s, y1, 2 * s, laneH); ctx.fillStyle = 'rgba(255,255,255,0.22)'; ctx.fillRect(x - 0.5 * s, y1 + laneH, s, pad); }
      if (a[4]) { ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.fillRect(x - s, y2, 2 * s, laneH); }
    }
    ctx.restore();
    if (!L.some((a) => a[1] || a[2])) {
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.font = Math.round(11 * s) + 'px Inter, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Press play to record both strips', W / 2, H / 2);
    }
  }

  /* ================= sources ================= */

  refocus(act, id) {
    const host = this.refs.srcList;
    if (!host) return;
    const b = host.querySelector('[data-act="' + act + '"][data-id="' + id + '"]');
    if (b && !b.disabled) b.focus();
    else { const p = host.querySelector('[data-src="' + id + '"] .osl-src__play'); if (p) p.focus(); }
  }

  syncHa() { if (this.ha) this.ha.update(); }

  winner() { if (!this.movie) return null; for (const id of this.order) if (this.enabled[id] && this.playing[id]) return SOURCES.find((s) => s.id === id); return null; }

  /* the app's Sources page, one row per source (pages.py PlayerRow): the
     switch that follows or ignores it, what it is and how it is recognised,
     the mode, intensity and area it plays with, and its place in the list */
  renderSources() {
    const host = this.refs.srcList;
    if (!host) return;
    const win = this.winner();
    /* FLIP: rows glide to their new place rather than jumping */
    const before = {};
    host.querySelectorAll('[data-src]').forEach((el) => { before[el.getAttribute('data-src')] = el.getBoundingClientRect().top; });
    host.innerHTML = '';
    this.order.forEach((id, idx) => {
      const src = SOURCES.find((x) => x.id === id);
      const on = this.enabled[id], playing = this.playing[id];
      const isWin = win && win.id === id, waiting = this.movie && on && playing && !isWin;
      const row = document.createElement('div');
      row.className = 'osl-src' + (isWin ? ' is-winner' : '') + (on ? '' : ' is-off') + (waiting ? ' is-waiting' : '');
      row.setAttribute('data-src', id);
      const modeKey = (src.modeNow.split(',')[0] || '').trim();
      const modeLbl = src.mode === 'Automatic' ? 'Automatic · ' + modeKey : src.mode;
      row.innerHTML =
        '<button type="button" class="osl-tsw' + (on ? ' is-on' : '') + '" data-act="srcToggle" data-id="' + id + '" role="switch" aria-checked="' + on + '" aria-label="Follow ' + src.name + '"><span></span></button>' +
        '<span class="osl-src__ic" aria-hidden="true">' + SRC_ICON[id] + '</span>' +
        '<span class="osl-src__main">' +
          '<span class="osl-src__top"><b class="osl-src__name">' + src.name + '</b>' +
            (isWin ? '<em class="osl-src__badge is-win"><i></i>Has the lights</em>' : waiting ? '<em class="osl-src__badge">Waiting</em>' : '') + '</span>' +
          '<span class="osl-src__meta">' + src.meta + '</span>' +
          '<span class="osl-src__chips">' +
            '<span class="osl-chip" style="--c:' + (MODE_C[modeKey] || '#60a5fa') + '">' + modeLbl + '</span>' +
            '<span class="osl-chip" style="--c:' + (INT_C[src.intensity.toLowerCase()] || '#f472b6') + '">' + src.intensity + '</span>' +
            '<span class="osl-chip osl-chip--area"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 21h6M10 18h4M12 3a6 6 0 0 0-3.5 10.9V16h7v-2.1A6 6 0 0 0 12 3Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>' + src.area + '</span>' +
          '</span>' +
        '</span>' +
        '<span class="osl-src__side">' +
          '<span class="osl-src__move">' +
            '<button type="button" data-act="srcUp" data-id="' + id + '" aria-label="Move ' + src.name + ' up"' + (idx === 0 ? ' disabled' : '') + '><svg viewBox="0 0 24 24"><path d="m6 15 6-6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
            '<button type="button" data-act="srcDown" data-id="' + id + '" aria-label="Move ' + src.name + ' down"' + (idx === this.order.length - 1 ? ' disabled' : '') + '><svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
          '</span>' +
          '<button type="button" class="osl-src__play' + (playing ? ' is-on' : '') + '" data-act="srcPlay" data-id="' + id + '" aria-pressed="' + playing + '">' +
            (playing ? '<i class="osl-eqi"><i></i><i></i><i></i></i>Playing' : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7L8 5Z" fill="currentColor"/></svg>Play on it') + '</button>' +
        '</span>';
      host.appendChild(row);
    });
    if (!this.reduced) {
      host.querySelectorAll('[data-src]').forEach((el) => {
        const was = before[el.getAttribute('data-src')];
        if (was == null) return;
        const dy = was - el.getBoundingClientRect().top;
        if (Math.abs(dy) < 1) return;
        el.animate([{ transform: 'translateY(' + dy + 'px)' }, { transform: 'none' }], { duration: 320, easing: 'cubic-bezier(.2,.7,.2,1)' });
      });
    }
    this.renderWinner(win);
  }

  /* who has the lights, said the way the app's Home page says it */
  renderWinner(win) {
    const out = this.refs.srcOut;
    if (!out) return;
    const prev = this.prevWinner;
    this.stepStatus(win);
    let html = '<span class="osl-srcout__top"><span class="cmu-kicker" style="margin:0">WHO HAS THE LIGHTS</span>' +
      '<span class="osl-gpill' + (win ? (this.hs === 'ghosting' ? ' is-ghost' : ' is-sync') : '') + '"><i></i>' + (win ? this.hs : 'idle') + '</span></span>';
    if (!this.movie) {
      html += '<b>Movie mode is off</b><p>Global sync was switched off in Home Assistant. Switch it back on and the top source that is playing takes the lights again.</p>';
      this.handover = null;
    } else if (!win) {
      html += '<b>The room is resting</b><p>Press <b>Play on it</b> on any source to hand it the entertainment area.</p>';
      if (prev && prev.kind !== 'pc') html += '<p>The ghost ' + prev.name + ' used is parked for 10 s, then closed.</p>';
      this.handover = null;
    } else {
      const waiting = this.order.filter((id) => this.playing[id] && this.enabled[id] && id !== win.id).map((id) => SOURCES.find((x) => x.id === id).name);
      html += '<b>' + win.name + '</b>';
      html += '<span class="osl-srcout__hs">Hue Sync · area ' + win.area + ' · mode ' + this.haMode(win) + ' · intensity ' + this.haIntensity(win) + '</span>';
      html += '<p>' + win.how + '</p>';
      if (this.session) html += '<p class="osl-srcout__ha">Changed from Home Assistant for this session: ' + Object.keys(this.session).map((k) => k + ' ' + this.session[k]).join(', ') + '. The next source starts with its own settings.</p>';
      if (prev && prev.id !== win.id) {
        let t = prev.area !== win.area ? 'A new area, so Hue Sync restarts once, silently (about 3 s). ' : 'Same area: the new settings apply at once. ';
        if (prev.kind !== 'pc') t += 'The ghost ' + prev.name + ' left behind is parked, and closed 10 s later unless it gets the lights straight back.';
        this.handover = { id: win.id, text: t };
      }
      if (this.handover && this.handover.id === win.id) html += '<p>' + this.handover.text + '</p>';
      if (waiting.length) html += '<p class="osl-srcout__wait">Also playing, lower in the list: ' + waiting.join(', ') + '. Move one to the top to hand it the lights.</p>';
    }
    out.innerHTML = html;
    out.classList.toggle('is-sync', !!win);
    this.prevWinner = win;
  }

  /* the status sensor, walked the way hue-ghost walks it: a Jellyfin source
     is "ghosting" while the ghost opens and Hue Sync starts on it, an app on
     the PC is captured straight away. A new source also starts a new session,
     so what Home Assistant changed for the last one is dropped. */
  stepStatus(win) {
    const id = win ? win.id : null;
    if (id === this.hsFor) return;
    this.hsFor = id;
    this.session = null;
    clearTimeout(this.hsTimer);
    if (!win) { this.hs = 'idle'; return; }
    this.lastHs = { mode: modeOf(win), intensity: win.intensity.toLowerCase() };
    if (HA_SRC[win.id].kind === 'pc') { this.hs = 'syncing'; return; }
    this.hs = 'ghosting';
    this.hsTimer = setTimeout(() => {
      this.hs = 'syncing';
      this.renderWinner(this.winner());
      this.syncHa();
    }, this.reduced ? 700 : 2400);
  }

  haMode(win) { return (this.session && this.session.mode) || (win ? modeOf(win) : this.lastHs.mode); }
  haIntensity(win) { return (this.session && this.session.intensity) || (win ? win.intensity.toLowerCase() : this.lastHs.intensity); }

  /* ================= Home Assistant ================= */

  /* the real hue-ghost-card (hue-synco-card.js, unchanged) on a stand-in
     Home Assistant (ha-mock.js) built from the sources above; both load when
     the section comes near the viewport */
  mountHa() {
    const host = this.refs.haCard, ha = this.cfg.ha;
    if (!host || !ha || !ha.mock || !ha.card) return;
    const start = () => {
      import(abs(ha.mock))
        .then((HA) => HA.loadCards(abs(ha.card), ['hue-ghost-card']).then(() => this.startHa(HA)))
        .catch((e) => {
          console.error('[hue-ghost] the Home Assistant demo failed to load', e);
          host.classList.add('is-failed');
        });
    };
    if (!('IntersectionObserver' in window)) { start(); return; }
    this.haIo = new IntersectionObserver((ents) => {
      if (!ents.some((e) => e.isIntersecting)) return;
      this.haIo.disconnect();
      this.haIo = null;
      start();
    }, { rootMargin: '600px 0px' });
    this.haIo.observe(host);
  }

  startHa(HA) {
    const dev = 'hue_ghost', reg = {};
    const add = (id, key) => { reg[id] = { entity_id: id, platform: 'hue_music_sync', translation_key: key, device_id: dev, has_entity_name: true, name: null, icon: null, entity_category: null, hidden: false }; };
    add('light.hue_ghost_global_sync', 'ghost_light');
    add('sensor.hue_ghost_sync_status', 'ghost_state');
    add('sensor.hue_ghost_sync_area', 'ghost_area');
    add('sensor.hue_ghost_active_source', 'ghost_source');
    add('sensor.hue_ghost_now_playing', 'ghost_now_playing');
    add('select.hue_ghost_mode', 'ghost_mode');
    add('select.hue_ghost_intensity', 'ghost_intensity');
    add('number.hue_ghost_sync_offset', 'ghost_offset');
    SOURCES.forEach((src) => add('switch.hue_ghost_' + HA_SRC[src.id].slug, 'ghost_binding'));
    this.mock = new HA.MockHass({
      states: this.haStates(),
      entities: reg,
      devices: { [dev]: { id: dev, name: 'Hue Ghost', name_by_user: null, manufacturer: 'hue-ghost', model: 'Software Hue Sync Box (Jellyfin)' } },
      translations: GHOST_TR,
      onService: (domain, service, data) => this.haService(domain, service, data)
    });
    const card = document.createElement('hue-ghost-card');
    card.setConfig({ type: 'custom:hue-ghost-card' });
    this.mock.attach(card);
    this.refs.haCard.replaceChildren(card);
    this.refs.haCard.classList.add('is-live');
    this.ha = { update: () => this.mock.replace(this.haStates()) };
    this.HA = HA;
    this.renderEnts();
    /* the card's icon asks HA for the more-info dialog; here the entity's row answers */
    if (this.refs.haDash) this.on(this.refs.haDash, 'hass-more-info', (e) => this.flashEnt(e.detail && e.detail.entityId));
  }

  /* every entity of the Hue Ghost device, read off the sources */
  haStates() {
    const win = this.winner(), live = win ? HA_SRC[win.id] : null;
    const mode = this.haMode(win), intensity = this.haIntensity(win);
    const known = (v) => (v == null ? 'unknown' : v);
    const out = {};
    out['light.hue_ghost_global_sync'] = { state: this.movie ? 'on' : 'off', attributes: {
      supported_color_modes: ['brightness'], color_mode: this.movie ? 'brightness' : null,
      brightness: this.movie ? Math.round(this.level * 2.55) : null,
      area: win ? win.area : null, mode, intensity, source: win ? win.name : null,
      now_playing: live ? live.title : null, syncing: this.hs === 'syncing',
      icon: 'mdi:television-ambient-light', friendly_name: 'Hue Ghost Global sync', supported_features: 0 } };
    out['sensor.hue_ghost_sync_status'] = { state: this.hs, attributes: {
      options: ['offline', 'idle', 'ghosting', 'syncing'], device_class: 'enum',
      now_playing: live ? live.title : null, active_source: win ? win.name : null, area: win ? win.area : null,
      icon: 'mdi:ghost', friendly_name: 'Hue Ghost Sync status' } };
    out['sensor.hue_ghost_sync_area'] = { state: known(win && win.area), attributes: { syncing: this.hs === 'syncing', icon: 'mdi:sofa', friendly_name: 'Hue Ghost Sync area' } };
    out['sensor.hue_ghost_active_source'] = { state: known(win && win.name), attributes: {
      kind: live ? live.kind : null, device: live ? live.device : null,
      followed: this.order.filter((id) => this.enabled[id]).map((id) => SOURCES.find((x) => x.id === id).name),
      icon: 'mdi:import', friendly_name: 'Hue Ghost Active source' } };
    out['sensor.hue_ghost_now_playing'] = { state: known(live && live.title), attributes: { source: win ? win.name : null, paused: false, icon: 'mdi:play-box-outline', friendly_name: 'Hue Ghost Now playing' } };
    out['select.hue_ghost_mode'] = { state: mode, attributes: { options: ['video', 'music', 'games'], icon: 'mdi:movie-filter', friendly_name: 'Hue Ghost Mode' } };
    out['select.hue_ghost_intensity'] = { state: intensity, attributes: { options: ['subtle', 'moderate', 'high', 'extreme'], icon: 'mdi:sine-wave', friendly_name: 'Hue Ghost Intensity' } };
    out['number.hue_ghost_sync_offset'] = { state: this.haOffset, attributes: { min: -2, max: 5, step: 0.05, mode: 'box', unit_of_measurement: 's', icon: 'mdi:timer-sand', friendly_name: 'Hue Ghost Sync offset' } };
    this.order.forEach((id, i) => {
      const src = SOURCES.find((x) => x.id === id), h = HA_SRC[id], act = !!(win && win.id === id);
      out['switch.hue_ghost_' + h.slug] = { state: this.enabled[id] ? 'on' : 'off', attributes: {
        active: act, priority: i, source: h.kind, area: src.area, device: h.device,
        mode: src.mode === 'Automatic' ? '' : modeOf(src), problems: [], now_playing: act ? h.title : null,
        icon: h.kind === 'pc' ? 'mdi:monitor-dashboard' : 'mdi:television-play', friendly_name: 'Hue Ghost ' + src.name } };
    });
    return out;
  }

  /* a service call from the card or the entities card, applied to the sources */
  haService(domain, service, data) {
    const id = [].concat(data.entity_id || [])[0] || '';
    if (id === 'light.hue_ghost_global_sync') {
      const was = this.movie;
      if (service === 'turn_off') this.movie = false;
      else if (service === 'toggle') this.movie = !this.movie;
      else if (service === 'turn_on') {
        this.movie = true;
        if (data.brightness_pct != null) this.level = Math.max(1, Math.min(100, Math.round(data.brightness_pct)));
        else if (data.brightness != null) this.level = Math.max(1, Math.min(100, Math.round(data.brightness / 2.55)));
      }
      this.haLog(was !== this.movie ? 'Global sync turned ' + (this.movie ? 'on' : 'off') : 'Global sync set to ' + this.level + '%');
      this.renderSources();
      this.syncHa();
      return true;
    }
    if (id === 'select.hue_ghost_mode' || id === 'select.hue_ghost_intensity') {
      const key = id === 'select.hue_ghost_mode' ? 'mode' : 'intensity';
      const win = this.winner();
      if (win) {
        this.session = Object.assign({}, this.session, { [key]: data.option });
        const own = key === 'mode' ? modeOf(win) : win.intensity.toLowerCase();
        if (this.session[key] === own) delete this.session[key];
        if (!Object.keys(this.session).length) this.session = null;
      } else this.lastHs[key] = data.option;
      this.haLog((key === 'mode' ? 'Mode' : 'Intensity') + ' changed to ' + GHOST_TR['ghost_' + key][data.option]);
      this.renderWinner(win);
      this.syncHa();
      return true;
    }
    if (id === 'number.hue_ghost_sync_offset') {
      this.haOffset = Math.round(Math.max(-2, Math.min(5, Number(data.value) || 0)) * 20) / 20;
      this.haLog('Sync offset set to ' + this.haOffset.toFixed(2) + ' s');
      this.syncHa();
      return true;
    }
    const sid = Object.keys(HA_SRC).find((k) => 'switch.hue_ghost_' + HA_SRC[k].slug === id);
    if (domain === 'switch' && sid) {
      this.enabled[sid] = service === 'toggle' ? !this.enabled[sid] : service === 'turn_on';
      this.haLog(SOURCES.find((x) => x.id === sid).name + ' turned ' + (this.enabled[sid] ? 'on' : 'off') + ', and in the Sources list above');
      this.renderSources();
      this.syncHa();
      return true;
    }
    return false;
  }

  haLog(text) { if (this.refs.haStatus) this.refs.haStatus.textContent = text; }

  /* an entities card for the same device, as Home Assistant draws one */
  renderEnts() {
    if (!this.refs.haRows || !this.mock || this.ents) return;
    this.ents = new this.HA.EntitiesCard(this.refs.haRows, this.mock, ENT_ROWS.map(([id, name, kind]) => ({ id, name, kind: kind || 'text' })));
  }

  flashEnt(id) { if (this.ents) this.ents.flash(id); }

}

HueGhostPage.ACTIONS = {
  play() {
    if (this.phase === 'syncing' || this.phase === 'starting') {
      this.tvRun = false; this.ghostRun = false; this.unlock();
      this.phase = 'paused'; this.renderState(); return;
    }
    if (this.phase === 'paused') { this.tvRun = true; this.ghostRun = true; this.phase = 'syncing'; this.renderState(); return; }
    const reuse = this.ghostOpen && (this.phase === 'standby' || this.phase === 'stopped');
    this.tvRun = true; this.tvStall = 0;
    if (!reuse) { this.ghostT = this.tvT + this.offset; this.ghostHist = []; }
    this.ghostOpen = true; this.ghostRun = true; this.ghostHold = 0;
    this.phase = 'starting';
    this.timer.starting = reuse ? 0.3 : 1.1;
    this.lastTvCut = null; this.lastLampCut = null;
    this.setVerdict(reuse
      ? 'The ghost was still parked, so it <b>picks up where it left off</b>, ready at once.'
      : 'The ghost renders first, then Hue Sync starts, so only the film reaches the lamps. Watch a cut land.');
    this.renderState();
  },
  stop() {
    this.tvRun = false; this.ghostRun = false; this.tvStall = 0; this.pendingSeek = null; this.ghostHold = 0; this.unlock();
    this.phase = 'stopped'; this.timer.lightsOff = 1.5;
    if (this.refs.npTime) this.refs.npTime.textContent = 'TV stopped. Lights off in 1.5 s';
    this.setVerdict('The lights go off <b>1.5 s</b> after the TV stops, and the ghost waits <b>10 s</b> in case the next episode starts.');
    this.renderState();
  },
  seek() {
    if (this.phase !== 'syncing') return;
    const actual = this.tvBuffer + (Math.random() - 0.5) * 0.16;
    this.tvT += 30; this.tvStall = actual; this.unlock();
    this.pendingSeek = { target: this.tvT, wait: 0.5, actual };
    this.setVerdict('The TV seeks and sits on a still frame while it buffers…');
  },
  offset(el) {
    const d = parseFloat(el.getAttribute('data-d'));
    const next = Math.round(Math.max(0, Math.min(3, this.offset + d)) * 100) / 100;
    if (this.ghostOpen) this.ghostT += next - this.offset;   /* "It applies instantly" */
    this.offset = next;
    this.renderOffset();
  },
  resetOffset() {
    if (this.ghostOpen) this.ghostT += this.tuned - this.offset;
    this.offset = this.tuned;
    this.renderOffset();
  },
  timeLock() {
    this.timeLock = !this.timeLock;
    if (!this.timeLock) this.unlock();
    this.renderTimeLock();
  },
  srcPlay(el) { const id = el.getAttribute('data-id'); this.playing[id] = !this.playing[id]; this.renderSources(); this.syncHa(); this.refocus('srcPlay', id); },
  srcToggle(el) { const id = el.getAttribute('data-id'); this.enabled[id] = !this.enabled[id]; this.renderSources(); this.syncHa(); this.refocus('srcToggle', id); },
  srcUp(el) { const id = el.getAttribute('data-id'), i = this.order.indexOf(id); if (i > 0) { this.order.splice(i, 1); this.order.splice(i - 1, 0, id); } this.renderSources(); this.syncHa(); this.refocus('srcUp', id); },
  srcDown(el) { const id = el.getAttribute('data-id'), i = this.order.indexOf(id); if (i >= 0 && i < this.order.length - 1) { this.order.splice(i, 1); this.order.splice(i + 1, 0, id); } this.renderSources(); this.syncHa(); this.refocus('srcDown', id); }
};

function boot(root) {
  if (!root || INSTANCES.has(root)) return;
  let cfg = {};
  const raw = root.querySelector('[data-hueghost-config]');
  if (raw) { try { cfg = JSON.parse(raw.textContent); } catch (e) { console.error('[hue-ghost] bad config', e); } }
  const page = new HueGhostPage(root, cfg);
  INSTANCES.set(root, page);
  page.mount();
}
function bootAll(scope) { (scope || document).querySelectorAll('[data-hueghost]').forEach(boot); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bootAll());
else bootAll();
document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-hueghost]').forEach((root) => { const inst = INSTANCES.get(root); if (inst) { inst.destroy(); INSTANCES.delete(root); } });
});
